import React, { useState } from 'react';
import { SearchIcon, FilterIcon, CalendarIcon, ClockIcon, UserIcon, HistoryIcon, AlertCircleIcon, PlusIcon, EditIcon, TrashIcon, ToggleLeftIcon, ToggleRightIcon, EyeIcon, CheckIcon, XIcon, ArrowUpRightIcon } from 'lucide-react';
import Card from '../../components/ui/Card';
import Modal from '../../components/ui/Modal';
import ExportMenu from '../../components/ui/ExportMenu';
interface AuditLog {
  id: string;
  action: 'create' | 'update' | 'delete' | 'activate' | 'deactivate' | 'approve' | 'reject' | 'escalate';
  entityType: 'rule' | 'alert' | 'transaction';
  entityId: string;
  entityName: string;
  timestamp: string;
  user: {
    id: string;
    name: string;
    email: string;
    role: string;
  };
  details: string;
  changes?: {
    field: string;
    oldValue: string;
    newValue: string;
  }[];
}
const RulesAuditLogs: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [actionFilter, setActionFilter] = useState<string>('all');
  const [entityFilter, setEntityFilter] = useState<string>('all');
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [dateRange, setDateRange] = useState({
    start: '',
    end: ''
  });
  // Mock data for audit logs
  const auditLogsData: AuditLog[] = [{
    id: 'LOG001',
    action: 'create',
    entityType: 'rule',
    entityId: 'RULE001',
    entityName: 'High Transaction Volume',
    timestamp: '2023-10-28T10:30:00Z',
    user: {
      id: 'U001',
      name: 'Admin User',
      email: 'admin@example.com',
      role: 'Administrator'
    },
    details: 'Created new rule for high transaction volume detection',
    changes: [{
      field: 'Rule Name',
      oldValue: '',
      newValue: 'High Transaction Volume'
    }, {
      field: 'Threshold',
      oldValue: '',
      newValue: '100'
    }, {
      field: 'Status',
      oldValue: '',
      newValue: 'Active'
    }]
  }, {
    id: 'LOG002',
    action: 'update',
    entityType: 'rule',
    entityId: 'RULE002',
    entityName: 'Large Transaction Amount',
    timestamp: '2023-10-28T09:45:00Z',
    user: {
      id: 'U002',
      name: 'Risk Analyst',
      email: 'analyst@example.com',
      role: 'Risk Analyst'
    },
    details: 'Updated threshold for large transaction amount rule',
    changes: [{
      field: 'Threshold',
      oldValue: '5000',
      newValue: '10000'
    }]
  }, {
    id: 'LOG003',
    action: 'deactivate',
    entityType: 'rule',
    entityId: 'RULE003',
    entityName: 'Same Customer Multiple Transactions',
    timestamp: '2023-10-28T08:15:00Z',
    user: {
      id: 'U001',
      name: 'Admin User',
      email: 'admin@example.com',
      role: 'Administrator'
    },
    details: 'Deactivated rule for same customer multiple transactions'
  }, {
    id: 'LOG004',
    action: 'approve',
    entityType: 'alert',
    entityId: 'ALERT001',
    entityName: 'High Transaction Volume Alert',
    timestamp: '2023-10-27T16:30:00Z',
    user: {
      id: 'U003',
      name: 'Compliance Officer',
      email: 'compliance@example.com',
      role: 'Compliance Officer'
    },
    details: 'Approved flagged transaction after review'
  }, {
    id: 'LOG005',
    action: 'reject',
    entityType: 'alert',
    entityId: 'ALERT002',
    entityName: 'Large Transaction Amount Alert',
    timestamp: '2023-10-27T14:45:00Z',
    user: {
      id: 'U002',
      name: 'Risk Analyst',
      email: 'analyst@example.com',
      role: 'Risk Analyst'
    },
    details: 'Rejected and blocked suspicious transaction'
  }, {
    id: 'LOG006',
    action: 'escalate',
    entityType: 'alert',
    entityId: 'ALERT003',
    entityName: 'Unusual Transaction Pattern Alert',
    timestamp: '2023-10-27T13:15:00Z',
    user: {
      id: 'U002',
      name: 'Risk Analyst',
      email: 'analyst@example.com',
      role: 'Risk Analyst'
    },
    details: 'Escalated alert to compliance team for further investigation'
  }, {
    id: 'LOG007',
    action: 'delete',
    entityType: 'rule',
    entityId: 'RULE004',
    entityName: 'Geographical Restriction',
    timestamp: '2023-10-27T11:30:00Z',
    user: {
      id: 'U001',
      name: 'Admin User',
      email: 'admin@example.com',
      role: 'Administrator'
    },
    details: 'Deleted obsolete geographical restriction rule'
  }];
  // Filter audit logs based on search query, action filter, and entity filter
  const filteredLogs = auditLogsData.filter(log => {
    const matchesSearch = log.entityName.toLowerCase().includes(searchQuery.toLowerCase()) || log.user.name.toLowerCase().includes(searchQuery.toLowerCase()) || log.details.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesAction = actionFilter === 'all' || log.action === actionFilter;
    const matchesEntity = entityFilter === 'all' || log.entityType === entityFilter;
    return matchesSearch && matchesAction && matchesEntity;
  });
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  const getActionIcon = (action: string) => {
    switch (action) {
      case 'create':
        return <PlusIcon className="h-4 w-4 text-green-500" data-id="element-7132" />;
      case 'update':
        return <EditIcon className="h-4 w-4 text-blue-500" data-id="element-7133" />;
      case 'delete':
        return <TrashIcon className="h-4 w-4 text-red-500" data-id="element-7134" />;
      case 'activate':
        return <ToggleRightIcon className="h-4 w-4 text-green-500" data-id="element-7135" />;
      case 'deactivate':
        return <ToggleLeftIcon className="h-4 w-4 text-gray-500" data-id="element-7136" />;
      case 'approve':
        return <CheckIcon className="h-4 w-4 text-green-500" data-id="element-7137" />;
      case 'reject':
        return <XIcon className="h-4 w-4 text-red-500" data-id="element-7138" />;
      case 'escalate':
        return <ArrowUpRightIcon className="h-4 w-4 text-purple-500" data-id="element-7139" />;
      default:
        return <HistoryIcon className="h-4 w-4 text-gray-500" data-id="element-7140" />;
    }
  };
  const getActionText = (action: string) => {
    return action.charAt(0).toUpperCase() + action.slice(1);
  };
  const getEntityTypeText = (entityType: string) => {
    switch (entityType) {
      case 'rule':
        return 'Rule';
      case 'alert':
        return 'Alert';
      case 'transaction':
        return 'Transaction';
      default:
        return entityType;
    }
  };
  const handleViewDetails = (log: AuditLog) => {
    setSelectedLog(log);
    setShowDetailModal(true);
  };
  const handleExport = (format: 'csv' | 'xlsx' | 'pdf') => {
    console.log(`Exporting audit logs in ${format} format`);
    // In a real app, this would trigger the export functionality
  };
  return <div className="space-y-6" data-id="element-7141">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between" data-id="element-7142">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-7143">
          Audit & Logs
        </h1>
        <div className="mt-3 sm:mt-0 flex items-center space-x-2" data-id="element-7144">
          <button className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-7145">
            <CalendarIcon className="h-4 w-4 mr-2" data-id="element-7146" />
            Date Range
          </button>
          <ExportMenu onExport={handleExport} data-id="element-7147" />
        </div>
      </div>
      <Card data-id="element-7148">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 space-y-3 md:space-y-0" data-id="element-7149">
          <div className="relative" data-id="element-7150">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none" data-id="element-7151">
              <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-7152" />
            </div>
            <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-800 focus:ring-blue-500 focus:border-blue-500" placeholder="Search logs..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-7153" />
          </div>
          <div className="flex items-center space-x-4" data-id="element-7154">
            <div className="flex items-center space-x-2" data-id="element-7155">
              <span className="text-sm text-gray-500 dark:text-gray-400" data-id="element-7156">
                Action:
              </span>
              <select className="block w-32 pl-3 pr-10 py-2 text-base border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-800 focus:ring-blue-500 focus:border-blue-500" value={actionFilter} onChange={e => setActionFilter(e.target.value)} data-id="element-7157">
                <option value="all" data-id="element-7158">All Actions</option>
                <option value="create" data-id="element-7159">Create</option>
                <option value="update" data-id="element-7160">Update</option>
                <option value="delete" data-id="element-7161">Delete</option>
                <option value="activate" data-id="element-7162">Activate</option>
                <option value="deactivate" data-id="element-7163">Deactivate</option>
                <option value="approve" data-id="element-7164">Approve</option>
                <option value="reject" data-id="element-7165">Reject</option>
                <option value="escalate" data-id="element-7166">Escalate</option>
              </select>
            </div>
            <div className="flex items-center space-x-2" data-id="element-7167">
              <span className="text-sm text-gray-500 dark:text-gray-400" data-id="element-7168">
                Entity:
              </span>
              <select className="block w-32 pl-3 pr-10 py-2 text-base border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-800 focus:ring-blue-500 focus:border-blue-500" value={entityFilter} onChange={e => setEntityFilter(e.target.value)} data-id="element-7169">
                <option value="all" data-id="element-7170">All Entities</option>
                <option value="rule" data-id="element-7171">Rules</option>
                <option value="alert" data-id="element-7172">Alerts</option>
                <option value="transaction" data-id="element-7173">Transactions</option>
              </select>
            </div>
            <button className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-7174">
              <FilterIcon className="h-4 w-4 mr-2" data-id="element-7175" />
              More Filters
            </button>
          </div>
        </div>
        <div className="overflow-x-auto" data-id="element-7176">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" data-id="element-7177">
            <thead className="bg-gray-50 dark:bg-gray-700" data-id="element-7178">
              <tr data-id="element-7179">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-7180">
                  Action
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-7181">
                  Entity
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-7182">
                  Description
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-7183">
                  User
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-7184">
                  Timestamp
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-7185">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700" data-id="element-7186">
              {filteredLogs.map(log => <tr key={log.id} className="hover:bg-gray-50 dark:hover:bg-gray-750" data-id="element-7187">
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-7188">
                    <div className="flex items-center" data-id="element-7189">
                      {getActionIcon(log.action)}
                      <span className="ml-2 text-sm font-medium text-gray-900 dark:text-white" data-id="element-7190">
                        {getActionText(log.action)}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-7191">
                    <div data-id="element-7192">
                      <span className="font-medium text-gray-900 dark:text-white" data-id="element-7193">
                        {getEntityTypeText(log.entityType)}
                      </span>
                      <div className="text-xs" data-id="element-7194">{log.entityName}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400 max-w-md truncate" data-id="element-7195">
                    {log.details}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-7196">
                    <div className="flex items-center" data-id="element-7197">
                      <div className="h-6 w-6 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-xs font-medium text-gray-700 dark:text-gray-300" data-id="element-7198">
                        {log.user.name.charAt(0)}
                      </div>
                      <div className="ml-2" data-id="element-7199">
                        <div className="font-medium text-gray-900 dark:text-white" data-id="element-7200">
                          {log.user.name}
                        </div>
                        <div className="text-xs" data-id="element-7201">{log.user.role}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-7202">
                    <div className="flex items-center" data-id="element-7203">
                      <ClockIcon className="h-4 w-4 mr-1 text-gray-400" data-id="element-7204" />
                      {formatDate(log.timestamp)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-7205">
                    <button onClick={() => handleViewDetails(log)} className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300" data-id="element-7206">
                      <EyeIcon className="h-5 w-5" data-id="element-7207" />
                    </button>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        {filteredLogs.length === 0 && <div className="py-8 text-center" data-id="element-7208">
            <AlertCircleIcon className="h-12 w-12 mx-auto text-gray-400" data-id="element-7209" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white" data-id="element-7210">
              No logs found
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400" data-id="element-7211">
              {searchQuery || actionFilter !== 'all' || entityFilter !== 'all' ? "Try adjusting your search or filters to find what you're looking for." : 'There are no audit logs to display at this time.'}
            </p>
          </div>}
      </Card>
      {/* Log Details Modal */}
      {selectedLog && <Modal isOpen={showDetailModal} onClose={() => setShowDetailModal(false)} title="Audit Log Details" size="lg" footer={<button onClick={() => setShowDetailModal(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-7213">
              Close
            </button>} data-id="element-7212">
          <div className="space-y-4" data-id="element-7214">
            <div className="flex items-center justify-between" data-id="element-7215">
              <div className="flex items-center" data-id="element-7216">
                {getActionIcon(selectedLog.action)}
                <h3 className="ml-2 text-lg font-medium text-gray-900 dark:text-white" data-id="element-7217">
                  {getActionText(selectedLog.action)}{' '}
                  {getEntityTypeText(selectedLog.entityType)}
                </h3>
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400" data-id="element-7218">
                {formatDate(selectedLog.timestamp)}
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-7219">
              <div data-id="element-7220">
                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2" data-id="element-7221">
                  Entity Information
                </h4>
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-md p-3" data-id="element-7222">
                  <div className="space-y-2" data-id="element-7223">
                    <div data-id="element-7224">
                      <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-7225">
                        Entity Type
                      </p>
                      <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-7226">
                        {getEntityTypeText(selectedLog.entityType)}
                      </p>
                    </div>
                    <div data-id="element-7227">
                      <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-7228">
                        Entity Name
                      </p>
                      <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-7229">
                        {selectedLog.entityName}
                      </p>
                    </div>
                    <div data-id="element-7230">
                      <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-7231">
                        Entity ID
                      </p>
                      <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-7232">
                        {selectedLog.entityId}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div data-id="element-7233">
                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2" data-id="element-7234">
                  User Information
                </h4>
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-md p-3" data-id="element-7235">
                  <div className="flex items-center mb-3" data-id="element-7236">
                    <div className="h-8 w-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-7237">
                      {selectedLog.user.name.charAt(0)}
                    </div>
                    <div className="ml-3" data-id="element-7238">
                      <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-7239">
                        {selectedLog.user.name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-7240">
                        {selectedLog.user.role}
                      </p>
                    </div>
                  </div>
                  <div data-id="element-7241">
                    <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-7242">
                      Email
                    </p>
                    <p className="text-sm text-gray-900 dark:text-white" data-id="element-7243">
                      {selectedLog.user.email}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div data-id="element-7244">
              <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2" data-id="element-7245">
                Action Details
              </h4>
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-md p-3" data-id="element-7246">
                <p className="text-sm text-gray-900 dark:text-white" data-id="element-7247">
                  {selectedLog.details}
                </p>
              </div>
            </div>
            {selectedLog.changes && selectedLog.changes.length > 0 && <div data-id="element-7248">
                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2" data-id="element-7249">
                  Changes Made
                </h4>
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-md p-3" data-id="element-7250">
                  <div className="overflow-x-auto" data-id="element-7251">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" data-id="element-7252">
                      <thead data-id="element-7253">
                        <tr data-id="element-7254">
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400" data-id="element-7255">
                            Field
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400" data-id="element-7256">
                            Old Value
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400" data-id="element-7257">
                            New Value
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-7258">
                        {selectedLog.changes.map((change, index) => <tr key={index} data-id="element-7259">
                            <td className="px-4 py-2 text-sm text-gray-900 dark:text-white" data-id="element-7260">
                              {change.field}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400" data-id="element-7261">
                              {change.oldValue || <span className="text-gray-400 dark:text-gray-500" data-id="element-7262">
                                  Empty
                                </span>}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-900 dark:text-white" data-id="element-7263">
                              {change.newValue}
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>}
          </div>
        </Modal>}
    </div>;
};
export default RulesAuditLogs;